
import React, { useState } from "react";
import { MessageSquare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type MarketingContent = Database['public']['Tables']['marketing_content']['Row'];

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ProductAIChatbotProps {
  contents: MarketingContent[];
}

export const ProductAIChatbot: React.FC<ProductAIChatbotProps> = ({ contents }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmbedOpen, setIsEmbedOpen] = useState(false);

  const truncateContent = (content: string): string => {
    const maxLength = 4000;
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "... (content truncated for length)";
  };

  const analyzeContent = async (content: MarketingContent): Promise<string> => {
    try {
      const response = await fetch(content.content_url);
      const text = await response.text();
      return `Content from ${content.name}:\n${text}`;
    } catch (error) {
      console.error(`Error analyzing content ${content.name}:`, error);
      return `Content from ${content.name}: Unable to analyze content`;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const contentAnalyses = await Promise.all(contents.map(analyzeContent));
      const contentContext = contentAnalyses.map(analysis => truncateContent(analysis)).join('\n\n');

      const { data, error } = await supabase.functions.invoke('ai-chat-completion', {
        body: {
          messages: [
            {
              role: 'system',
              content: 'You are a fair and expert analyst who provides balanced advice on the pros and cons of products. Analyze the provided content and respond to user questions with clear, unbiased insights. Keep responses concise.'
            },
            {
              role: 'user',
              content: `Here is the content to analyze:\n\n${contentContext}\n\nUser question: ${userMessage}`
            }
          ]
        }
      });

      if (error) throw error;

      const aiResponse = data?.choices?.[0]?.message?.content || 'No response received.';
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to get AI response. Please try again.');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an error while processing your request. Please try again or break down your question into smaller parts.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 w-[60%] p-3 flex flex-col h-[545px]">
      <div className="mb-4 flex justify-between items-center">
        <h2 className="font-semibold text-base text-white">AI Product Chatbot</h2>
      </div>
      
      {/* Chat messages area */}
      <ScrollArea className="flex-grow p-2 border border-primary/20 rounded-lg bg-background/5 h-[200px] mb-4">
        {messages.map((message, i) => (
          <div
            key={i}
            className={`mb-2 p-2 rounded-lg text-[12px] text-primary-light ${
              message.role === 'user' ? 'bg-primary/10 ml-12' : 'bg-secondary/50 mr-12'
            }`}
          >
            <div className="whitespace-pre-line">{message.content}</div>
          </div>
        ))}
        {messages.length === 0 && (
          <div className="text-center text-primary-light py-4">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-[12px]">Ask me questions about the contents. Some examples: 1) Compare the products, 2) tell me the pros and cons of the products. 3) Tell me how to position the [product name] against [another product name]</p>
          </div>
        )}
        {isLoading && (
          <div className="flex justify-center items-center p-2">
            <Loader2 className="w-4 h-4 animate-spin text-primary-light" />
          </div>
        )}
      </ScrollArea>
      
      
      {/* Chat input */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask me a question"
          disabled={isLoading}
          className="flex-grow bg-background/5 border-white text-[12px] text-white placeholder:text-white/50 min-h-[70px] resize-none"
        />
        <Button
          type="submit"
          className="bg-primary hover:bg-primary/90 text-slate-950 font-normal text-xs"
          disabled={isLoading || !input.trim()}
        >
          Send
        </Button>
      </form>

      {/* Embed dialog */}
      <Dialog open={isEmbedOpen} onOpenChange={setIsEmbedOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Embed AI Chatbot</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-primary-light">
              Copy and paste this code into your website where you want the chatbot to appear:
            </p>
            <div className="relative">
              <pre className="bg-secondary/50 p-4 rounded-lg overflow-x-auto text-xs max-w-full">
                <code className="whitespace-pre-wrap break-all">
                  {`<iframe src="https://example.com/chatbot" width="100%" height="600px" frameborder="0"></iframe>`}
                </code>
              </pre>
            </div>
            <div className="flex justify-start">
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(`<iframe src="https://example.com/chatbot" width="100%" height="600px" frameborder="0"></iframe>`);
                  toast.success("Embed code copied to clipboard");
                  setIsEmbedOpen(false);
                }}
                className="bg-primary hover:bg-primary/90 text-black"
              >
                Copy Code
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
